<script setup lang="ts">
import { computed, inject, toRefs, watchEffect } from 'vue';
import { storeToRefs } from 'pinia';
import vtkPlane from '@kitware/vtk.js/Common/DataModel/Plane';
import { SlabTypes } from '@kitware/vtk.js/Rendering/Core/ImageResliceMapper/Constants';
import type { Vector3 } from '@kitware/vtk.js/types';

import { useImage } from '@/src/composables/useCurrentImage';
import { useResliceRepresentation } from '@/src/core/vtk/useResliceRepresentation';
import { useWindowingConfig } from '@/src/composables/useWindowingConfig';
import { VtkViewContext } from '@/src/components/vtk/context';
import { useCrosshairsToolStore } from '@/src/store/tools/crosshairs';
import { useViewStore } from '@/src/store/views';
import { LPSAxis } from '@/src/types/lps';
import { ViewInfo2D } from '@/src/types/views';
import { Maybe } from '@/src/types';

interface Props {
  viewId: string;
  imageId: Maybe<string>;
}

const props = defineProps<Props>();
const { viewId: viewID, imageId: imageID } = toRefs(props);

const view = inject(VtkViewContext);
if (!view) throw new Error('No VtkView');

const { imageData } = useImage(imageID);

const crosshairsStore = useCrosshairsToolStore();
const { position } = storeToRefs(crosshairsStore);
const viewStore = useViewStore();

// VolView is LPS internally, so the MPR plane normals are the LPS unit
// directions. We render three image-reslice planes through the volume so the
// user can see exactly where each 2D MPR slice sits in the 3D scene.
const PLANES: ReadonlyArray<{ axis: LPSAxis; normal: Vector3 }> = [
  { axis: 'Sagittal', normal: [1, 0, 0] }, // Left/Right
  { axis: 'Coronal', normal: [0, 1, 0] }, // Anterior/Posterior
  { axis: 'Axial', normal: [0, 0, 1] }, // Superior/Inferior
];

// Build one reslice representation per plane. Each call hooks its own actor
// into the renderer and disposes on scope teardown, so unmounting the
// component (when the crosshair tool deactivates) tears the planes back down.
const reps = PLANES.map(() => useResliceRepresentation(view, imageData));

reps.forEach((rep, i) => {
  // Single-slice sampling: a one-voxel-thick max-intensity slab through the
  // volume gives a crisp anatomical cross-section without averaging.
  rep.mapper.setSlabType(SlabTypes.MAX);
  rep.mapper.setSlabThickness(1);

  // Each plane gets its own slicePlane, kept aligned with the LPS axis.
  const slicePlane = vtkPlane.newInstance();
  slicePlane.setNormal(PLANES[i].normal);
  rep.mapper.setSlicePlane(slicePlane);

  // Keep the planes from z-fighting with the volume rendering or each other.
  rep.mapper.setResolveCoincidentTopologyToPolygonOffset();
  rep.mapper.setRelativeCoincidentTopologyPolygonOffsetParameters(-1, -1);

  // Exclude these planes from the WidgetManager's hardware-selector pass.
  // vtkImageResliceMapper's picking shader path crashes inside the 3D view
  // (null shader program -> isUniformUsed throws -> WebGL context loss and
  // every view goes blank), and these planes are visualization-only so the
  // user never needs to pick them anyway.
  rep.actor.setPickable(false);
});

// Mirror each plane's W/L to the matching 2D MPR view bound to the same image
// so the 3D cross-sections look identical to the 2D panels. When no such
// 2D view exists, fall back to this volume view's id — `useWindowingConfig`
// then resolves to DICOM/auto-stats defaults instead of the all-black
// uninitialized W/L.
const wlSourceViewIds = PLANES.map(({ axis }) =>
  computed(() => {
    const match = viewStore
      .getAllViews()
      .find(
        (v): v is ViewInfo2D =>
          v.type === '2D' &&
          v.options.orientation === axis &&
          v.dataID === imageID.value
      );
    return match?.id ?? viewID.value;
  })
);

const wlConfigs = wlSourceViewIds.map((id) => useWindowingConfig(id, imageID));

watchEffect(() => {
  if (!imageData.value) return;
  reps.forEach((rep, i) => {
    rep.property.setColorLevel(wlConfigs[i].level.value);
    rep.property.setColorWindow(wlConfigs[i].width.value);
  });
});

// The three planes intersect at the crosshair's world position, so a single
// origin update keeps them all in sync as the user drags the crosshair widget.
const planeOrigin = computed<Vector3>(() => position.value);

watchEffect(() => {
  if (!imageData.value) return;
  const origin = planeOrigin.value;
  reps.forEach((rep) => {
    const plane = rep.mapper.getSlicePlane();
    if (plane) plane.setOrigin(origin);
  });
  view.requestRender();
});
</script>

<template>
  <slot></slot>
</template>
